import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import type { SecondFactor, SecondFactorMethod } from '@shared/api/contracts/security';
import { SecondFactorDialog } from '../ui/second-factor-dialog';
import type { SecondFactorAsk } from './run-second-factor-action';

interface PendingAsk extends SecondFactorAsk {
  resolve: (value: SecondFactor | null) => void;
}

interface SecondFactorContextValue {
  ask: (input: SecondFactorAsk) => Promise<SecondFactor | null>;
}

const SecondFactorContext = createContext<SecondFactorContextValue | null>(null);

export function SecondFactorProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingAsk | null>(null);

  const value = useMemo<SecondFactorContextValue>(
    () => ({
      ask: (input) =>
        new Promise((resolve) => {
          setPending({ ...input, resolve });
        }),
    }),
    [],
  );

  return (
    <SecondFactorContext.Provider value={value}>
      {children}
      <SecondFactorDialog
        open={Boolean(pending)}
        purpose={pending?.purpose ?? ''}
        methods={(pending?.methods ?? ['totp']) as SecondFactorMethod[]}
        invalid={pending?.invalid}
        sendEmail={pending?.sendEmail}
        onSubmit={(factor) => {
          pending?.resolve(factor);
          setPending(null);
        }}
        onCancel={() => {
          pending?.resolve(null);
          setPending(null);
        }}
      />
    </SecondFactorContext.Provider>
  );
}

// Hook lives next to the provider so the dialog state stays in one place.
// eslint-disable-next-line react-refresh/only-export-components
export function useSecondFactorPrompt(): SecondFactorContextValue {
  const value = useContext(SecondFactorContext);
  if (!value) {
    throw new Error('useSecondFactorPrompt must be used within SecondFactorProvider');
  }
  return value;
}
